import { useMemo } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useBranch } from "@/context/BranchContext";
import { useOrders } from "@/context/OrdersContext";
import { useSettings } from "@/context/SettingsContext";
import { ORDER_STATUS, formatTimeAgo, vatBreakdown } from "@/pages/branch/orders/constants";
import type { OrderStatus } from "@/mocks/branch/orders";
import { mockBranchInfo } from "@/mocks/branch/employee";
import StatCard from "./components/StatCard";
import AccessDenied from "@/pages/branch/access-denied/page";
import {
  canViewDashboard,
  canViewMenu,
  canViewOrders,
  canViewSettings,
} from "@/config/permissions";

const ACTIVE_STATUSES: OrderStatus[] = ["new", "preparing", "almost_ready", "ready", "out_for_delivery"];
const STATUS_ORDER: OrderStatus[] = ["new", "preparing", "almost_ready", "ready"];

interface QuickItem {
  key: string;
  label: string;
  desc: string;
  icon: string;
  to: string;
  accent: string;
  show: boolean;
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { employee } = useBranch();
  const { orders, openOrderDetail } = useOrders();
  const { settings } = useSettings();

  const stats = useMemo(() => {
    const active = orders.filter((o) => ACTIVE_STATUSES.includes(o.status));
    const revenue = orders.reduce((sum, o) => sum + o.total, 0);
    const completed = orders.filter((o) => o.status === "picked_up").length;
    const notPicked = orders.filter((o) => o.status === "not_picked").length;
    const rated = orders.filter((o) => typeof o.rating === "number");
    const avgRating = rated.length
      ? (rated.reduce((sum, o) => sum + (o.rating ?? 0), 0) / rated.length).toFixed(1)
      : "—";
    const { vat } = vatBreakdown(revenue);

    const statusCounts: Record<string, number> = {};
    for (const s of STATUS_ORDER) {
      statusCounts[s] = orders.filter((o) => o.status === s).length;
    }

    return { active, revenue, vat, completed, notPicked, avgRating, statusCounts };
  }, [orders]);

  const recentOrders = useMemo(
    () => [...orders].sort((a, b) => a.minutesAgo - b.minutesAgo).slice(0, 5),
    [orders],
  );

  if (!employee) {
    return <Navigate to="/" replace />;
  }

  const permissions = employee.permissions;
  if (!canViewDashboard(permissions)) {
    return <AccessDenied />;
  }

  // سيناريو عرض «كاشير (أمين صندوق)»: صفحة رئيسية مبسّطة — عرض فقط ولا يغيّر منطق الصلاحيات.
  const isCashierScenario = employee.role === "cashier";
  const CASHIER_QUICK_KEYS = ["pos", "orders", "notifications"];

  const emergency = settings.emergencyClosure.active;
  const branchState: { label: string; cls: string; dot: string } = emergency
    ? { label: "مغلق مؤقتًا", cls: "bg-red-100 text-red-700", dot: "bg-red-500" }
    : mockBranchInfo.status === "disabled"
      ? { label: "معطل", cls: "bg-foreground-200 text-foreground-600", dot: "bg-foreground-500" }
      : mockBranchInfo.status === "closed"
        ? { label: "مغلق", cls: "bg-secondary-100 text-secondary-900", dot: "bg-secondary-500" }
        : { label: "مفتوح ويستقبل الطلبات", cls: "bg-primary-100 text-primary-700", dot: "bg-primary-500" };

  const quickItems: QuickItem[] = [
    { key: "pos", label: "شاشة الكاشير", desc: "إنشاء طلب محلي أو سفري", icon: "ri-computer-line", to: "/pos", accent: "bg-primary-100 text-primary-700", show: permissions.pos },
    { key: "orders", label: "الطلبات", desc: "متابعة الطلبات الواردة وتحديث الحالات", icon: "ri-file-list-3-line", to: "/orders", accent: "bg-accent-100 text-accent-900", show: canViewOrders(permissions) },
    { key: "menu", label: "المنيو", desc: "التحكم في توفر المنتجات والكميات", icon: "ri-restaurant-2-line", to: "/menu", accent: "bg-secondary-100 text-secondary-900", show: canViewMenu(permissions) },
    { key: "reports", label: "التقارير", desc: "المبيعات وتوزيع الطلبات والتقييمات", icon: "ri-bar-chart-2-line", to: "/reports", accent: "bg-primary-100 text-primary-700", show: permissions.reports },
    { key: "settings", label: "إعدادات الفرع", desc: "ساعات العمل وسياسة الاستلام", icon: "ri-settings-3-line", to: "/settings", accent: "bg-background-200 text-foreground-600", show: canViewSettings(permissions) },
    { key: "notifications", label: "الإشعارات", desc: "تنبيهات الطلبات والتقييمات والنظام", icon: "ri-notification-3-line", to: "/notifications", accent: "bg-accent-100 text-accent-900", show: true },
  ];
  const visibleQuick = quickItems.filter((q) =>
    isCashierScenario ? CASHIER_QUICK_KEYS.includes(q.key) && q.show : q.show,
  );

  const openOrder = (id: string) => {
    openOrderDetail(id);
    navigate("/orders");
  };

  return (
    <div className="max-w-[1400px] mx-auto space-y-5 md:space-y-6">
      {/* Welcome + branch status */}
      <div className="relative overflow-hidden rounded-card border border-background-200/60 bg-gradient-to-l from-primary-100/60 via-background-50 to-background-50 px-5 py-5 md:px-7 md:py-6 animate-fade-slide-up">
        <div className="pointer-events-none absolute -top-16 -left-10 w-52 h-52 rounded-full bg-accent-100/50 blur-2xl"></div>
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900">أهلاً، {employee?.name}</h2>
            <p className="mt-1 text-sm text-foreground-500">
              {employee?.storeName} — {employee?.branchName}
            </p>
          </div>
          {isCashierScenario ? null : (
            <span className={`inline-flex items-center gap-2 self-start sm:self-auto px-4 h-10 rounded-full text-sm font-bold ${branchState.cls}`}>
              <span className={`w-2.5 h-2.5 rounded-full ${branchState.dot} ${emergency ? "animate-pulse" : ""}`}></span>
              {branchState.label}
            </span>
          )}
        </div>
      </div>

      {/* KPI cards */}
      {isCashierScenario ? (
        <div className="grid grid-cols-2 gap-3 md:gap-4">
          <StatCard label="الطلبات النشطة" value={String(stats.active.length)} icon="ri-pulse-line" hint="جارية الآن" accent="secondary" />
          <StatCard label="الطلبات الجديدة" value={String(stats.statusCounts.new)} icon="ri-notification-3-line" hint="بانتظار بدء التحضير" accent="neutral" />
        </div>
      ) : (
        <div className="grid grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4">
          <StatCard label="طلبات اليوم" value={String(orders.length)} icon="ri-file-list-3-line" hint={`${stats.completed} مكتملة · ${stats.notPicked} لم تُستلم`} accent="primary" />
          <StatCard label="مبيعات اليوم" value={stats.revenue.toFixed(2)} icon="ri-money-dollar-circle-line" hint={`ر.س · ضريبة ${stats.vat.toFixed(2)}`} accent="accent" />
          <StatCard label="الطلبات النشطة" value={String(stats.active.length)} icon="ri-pulse-line" hint="جارية الآن" accent="secondary" />
          <StatCard label="الطلبات الجديدة" value={String(stats.statusCounts.new)} icon="ri-notification-3-line" hint="بانتظار بدء التحضير" accent="neutral" />
          <StatCard label="الطلبات المكتملة اليوم" value={String(stats.completed)} icon="ri-checkbox-circle-line" hint="مُستلَمة بنجاح" accent="primary" />
          <StatCard label="متوسط تقييم الفرع" value={stats.avgRating} icon="ri-star-line" hint="من أصل 5" accent="accent" />
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 md:gap-6">
        {/* Active orders */}
        <div className="xl:col-span-2 bg-background-50 rounded-card border border-background-200/70 shadow-card p-4 md:p-5 animate-fade-slide-up">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-base font-bold text-foreground-900 flex items-center gap-2">
              <i className="ri-pulse-line text-primary-700"></i>
              حالة الطلبات النشطة
            </h3>
            <button
              type="button"
              onClick={() => navigate("/orders")}
              className="text-sm font-semibold text-primary-700 hover:underline cursor-pointer whitespace-nowrap"
            >
              عرض الكل
            </button>
          </div>

          {stats.active.length === 0 ? (
            <div className="flex flex-col items-center justify-center text-center py-12 rounded-card bg-background-100 border border-dashed border-background-300">
              <div className="w-14 h-14 rounded-full bg-background-200 flex items-center justify-center">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="mt-3 text-sm font-bold text-foreground-800">لا توجد طلبات نشطة حاليًا</p>
              <p className="mt-1 text-xs text-foreground-500">ستظهر الطلبات الجديدة هنا فور وصولها</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {STATUS_ORDER.map((status) => {
                const cfg = ORDER_STATUS[status];
                return (
                  <button
                    key={status}
                    type="button"
                    onClick={() => navigate("/orders")}
                    title={`عرض طلبات «${cfg.label}»`}
                    className="rounded-card border border-background-200 p-4 text-right hover:border-background-300 transition-colors cursor-pointer"
                  >
                    <span className="inline-flex items-center gap-1.5" style={{ color: cfg.color }}>
                      <i className={`${cfg.icon} text-base`}></i>
                    </span>
                    <p className="mt-2 text-2xl font-bold text-foreground-900">{stats.statusCounts[status]}</p>
                    <p className="mt-0.5 text-xs text-foreground-600">{cfg.label}</p>
                  </button>
                );
              })}
            </div>
          )}

          {recentOrders.length > 0 ? (
            <div className="mt-6">
              <h4 className="text-sm font-bold text-foreground-700 mb-3">أحدث الطلبات</h4>
              <div className="divide-y divide-background-200">
                {recentOrders.map((order) => {
                  const cfg = ORDER_STATUS[order.status];
                  return (
                    <button
                      key={order.id}
                      type="button"
                      onClick={() => openOrder(order.id)}
                      className="w-full py-2.5 md:py-3 flex items-center gap-2 md:gap-3 text-right hover:bg-background-100 rounded-btn px-1.5 md:px-2 transition-colors cursor-pointer"
                    >
                      <span className="w-12 md:w-14 text-sm font-bold text-foreground-900 shrink-0" dir="ltr">
                        #{order.number}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground-800 truncate">{order.customerName}</p>
                        <p className="text-xs text-foreground-500">{formatTimeAgo(order.minutesAgo)}</p>
                      </div>
                      <span className="px-2.5 py-1 rounded-full text-xs font-bold whitespace-nowrap" style={{ backgroundColor: cfg.bg, color: cfg.color }}>
                        {cfg.label}
                      </span>
                      <span className="text-sm font-bold text-foreground-900 whitespace-nowrap" dir="ltr">
                        {order.total.toFixed(2)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>

        {/* Quick access */}
        <div className="bg-background-50 rounded-card border border-background-200/70 shadow-card p-4 md:p-5 animate-fade-slide-up">
          <h3 className="text-base font-bold text-foreground-900 mb-4">وصول سريع</h3>
          <div className="space-y-3">
            {visibleQuick.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => navigate(item.to)}
                className="w-full flex items-center gap-3 p-3 rounded-card border border-background-200 hover:border-background-300 hover:bg-background-100 transition-colors cursor-pointer text-right"
              >
                <div className={`w-11 h-11 rounded-btn flex items-center justify-center shrink-0 ${item.accent}`}>
                  <i className={`${item.icon} text-xl`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground-900">{item.label}</p>
                  <p className="text-xs text-foreground-500 mt-0.5">{item.desc}</p>
                </div>
                <i className="ri-arrow-left-s-line text-foreground-300 text-xl"></i>
              </button>
            ))}
          </div>

          {emergency ? (
            <div className="mt-4 p-3 rounded-btn bg-red-50 border border-red-100 text-xs text-red-700 leading-relaxed">
              <i className="ri-alert-line mr-1"></i>
              الفرع مغلق مؤقتًا: {settings.emergencyClosure.reason || "بدون سبب محدد"}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}